import React from 'react';

import { FmButton } from '../FmButton';
import { FmText } from '../FmComposition';
import { FmFormFlowDialog } from '../FmFormFlowDialog';
import { FmModal } from '../FmModal';
import type {
  FmOperationalDialogDescriptor,
  FmOperationalWorkspaceDescriptor,
} from '../FmOperationalWorkspaceRoot.types';

/**
 * A confirmation surface for one workspace dialog descriptor. `presentation`
 * is `overlay` on the real workspace; the gallery renders the same surface
 * `inline` so a destructive and a benign dialog can be compared side by side.
 */
export function OperationalWorkspaceDialogSurface({
  dialog,
  internallyOpen,
  inhibited,
  presentation = 'overlay',
  onDismiss,
  onConfirm,
  onAlternate,
}: {
  dialog: FmOperationalDialogDescriptor;
  internallyOpen: boolean;
  inhibited: boolean;
  presentation?: 'overlay' | 'inline';
  onDismiss: () => void;
  onConfirm: () => void;
  onAlternate?: () => void;
}): React.ReactElement | null {
  const open = dialog.open || internallyOpen;
  const alternateFooter = dialog.alternateLabel ? (
    <>
      <FmButton variant="bordered" onClick={onDismiss} data-testid="fm-modal-cancel">
        {dialog.cancelLabel}
      </FmButton>
      <FmButton
        variant="default"
        danger={dialog.alternateDestructive}
        onClick={onAlternate}
        disabled={inhibited}
        data-testid="fm-modal-alternate"
      >
        {dialog.alternateLabel}
      </FmButton>
      <FmButton
        variant="primary"
        onClick={onConfirm}
        disabled={inhibited}
        data-testid="fm-modal-ok"
      >
        {dialog.confirmLabel}
      </FmButton>
    </>
  ) : undefined;
  return (
    <FmModal
      open={open}
      title={dialog.title}
      onCancel={onDismiss}
      onOk={onConfirm}
      okDisabled={inhibited}
      okText={dialog.confirmLabel}
      cancelText={dialog.cancelLabel}
      footer={alternateFooter}
      dismissible={dialog.dismissible ?? !dialog.destructive}
      tone={dialog.destructive ? 'destructive' : 'default'}
      presentation={presentation}
      data-testid={
        presentation === 'inline'
          ? `operational-dialog-specimen-${dialog.id}`
          : `operational-dialog-${dialog.id}`
      }
    >
      <FmText>{dialog.body}</FmText>
    </FmModal>
  );
}

export function OperationalWorkspaceDialogs({
  descriptor,
  openDialogId,
  openDialogSnapshot,
  inhibited,
  setOpenDialogId,
  restoreDialogTrigger,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  openDialogId: string | null;
  openDialogSnapshot: FmOperationalDialogDescriptor | null;
  inhibited: boolean;
  setOpenDialogId: (id: string | null) => void;
  restoreDialogTrigger: () => void;
}): React.ReactElement {
  const formFlow = descriptor.formFlow;
  return (
    <>
      {descriptor.dialogs.map((dialog) => {
        const presentedDialog =
          openDialogId === dialog.id && openDialogSnapshot?.id === dialog.id
            ? openDialogSnapshot
            : dialog;
        return (
          <OperationalWorkspaceDialogSurface
            key={presentedDialog.id}
            dialog={presentedDialog}
            internallyOpen={openDialogId === dialog.id}
            inhibited={inhibited}
            onDismiss={() => {
              setOpenDialogId(null);
              descriptor.onEvent?.({
                kind: 'dialog-dismiss',
                dialogId: dialog.id,
              });
              restoreDialogTrigger();
            }}
            onConfirm={() => {
              if (inhibited) return;
              setOpenDialogId(null);
              descriptor.onEvent?.({
                kind: 'dialog-confirm',
                dialogId: presentedDialog.id,
                actionId: presentedDialog.actionId,
                expectedRevisionId: presentedDialog.expectedRevisionId,
                expectedPayloadRevision: presentedDialog.expectedPayloadRevision,
                expectedDeleteConsequenceRevision:
                  presentedDialog.expectedDeleteConsequenceRevision,
              });
              restoreDialogTrigger();
            }}
            onAlternate={
              presentedDialog.alternateActionId
                ? () => {
                    if (inhibited) return;
                    setOpenDialogId(null);
                    descriptor.onEvent?.({
                      kind: 'dialog-alternate',
                      dialogId: presentedDialog.id,
                      actionId: presentedDialog.alternateActionId!,
                      expectedRevisionId: presentedDialog.expectedRevisionId,
                    });
                    restoreDialogTrigger();
                  }
                : undefined
            }
          />
        );
      })}
      {formFlow ? (
        <FmFormFlowDialog
          open={formFlow.open}
          title={formFlow.title}
          closeLabel={formFlow.closeLabel}
          size={formFlow.size}
          descriptor={formFlow.flow}
          onClose={() => {
            descriptor.onEvent?.({
              kind: 'dialog-dismiss',
              dialogId: formFlow.id,
            });
            restoreDialogTrigger();
          }}
          data-testid={formFlow.id}
        />
      ) : null}
    </>
  );
}
